import React, { createContext, useContext, useEffect, useState } from 'react';
import { ANSWERED, Bereavement, CarePlanFrequency, CarePlanProblems, ClearScripts, Contents, DocLink, Measures, NONE, PatientAssistance, ProgressNotes, STRING_FALSE, STRING_TRUE } from '@mummssoftware/utils/constants';
import { notesIncluded } from '@mummssoftware/utils/utils';
import { DialogBox, Loader } from '@mummssoftware/common-ui';
import { CPPGlobalContext, CertGlobalContext, GlobalContext, TaskGlobalContext, VFGlobalContext } from '@mummssoftware/global-context';
import { printPDF } from '../DashboardPDF/utils/printUtils';
import { MummsPDFGlobalContext } from '../MummsPDFGlobalContext';
import { DashboardPDFDocument } from '../DashboardPDF';
import { INCLUDE_PDF_BER_NOTES, INCLUDE_PDF_CERT_APP, INCLUDE_PDF_CPF_NOTES, INCLUDE_PDF_MEDICATIONS_NOTES, INCLUDE_PDF_TASKS_NOTES } from '../MummsPDFGlobalContext/constants';
import { clearPdfData, getAsyncPdfData } from '../DashboardPDF/utils/dataUtils';
export const PrintPDFContext = /*#__PURE__*/createContext({
  printDaChart: null
});
/* 
  Context gives you a printDaChart function. Pass it a widgetConfig (list of string component
  names) and a printConfig (the PrintInterface config dto) and it'll print with ReactPDF.
  
  Preferred when printing from a standalone icon, prefer printComponents to avoid excess
  API calls.
*/

var _ref = /*#__PURE__*/React.createElement(Loader, null);

export const PDFContext = ({
  children,
  patient,
  currentUser,
  agency,
  observation,
  pim,
  keycloak,
  clearscripts
}) => {
  const {
    pdfProgressNotes,
    pdfPSOComponents,
    asyncPdfDataReady,
    pdfRgHash,
    pdfRgHashPrint,
    pdfCpfNotes,
    pdfCarePlanProblems,
    pdfMedications,
    pdfMedicationsNotes,
    pdfBereavNotes,
    pdfCareGiversInfo,
    pdfCertAppSnapshot,
    pdfMeasuresGraphImages,
    jsonMeasures,
    historicalRows,
    pdfWeeklyTasks,
    includePdfTasksNotes,
    pdfTasksNotes,
    getAllRgHashes,
    dispatchReactPDF,
    pdfFacesheetData
  } = useContext(MummsPDFGlobalContext);
  const {
    cppNotesGlobal
  } = useContext(CPPGlobalContext);
  const {
    patientActiveCPPTasks
  } = useContext(TaskGlobalContext);
  const {
    measuresGlobal,
    patientStandingOrdersGlobal,
    carePlanFrequenciesGlobal
  } = useContext(GlobalContext);
  const globalContext = useContext(GlobalContext);
  const {
    patientCertHistory
  } = useContext(CertGlobalContext);
  const {
    roleGroupsGlobal
  } = useContext(VFGlobalContext);
  const [localWidgetConfig, setWidgetConfig] = useState([]);
  const [localPrintConfig, setPrintConfig] = useState({});
  const [loading, setLoading] = useState(false); // taken straight from from PrintInterface. Didn't refactor this function into a utils file
  // there would be so many params to pass in context variables.
  // todo: eliminate duplicated code

  const getWidgetConfig = (printConfig, widgetConfig, fromDateFormatted, toDateFormatted // dispatchReactPDF: any,
  ) => {
    const asyncWidgetPayload = [];

    if (widgetConfig && widgetConfig.includes(Bereavement)) {
      const BereavementPrint = printConfig === null || printConfig === void 0 ? void 0 : printConfig.Bereavement;

      if (notesIncluded(BereavementPrint)) {
        dispatchReactPDF({
          type: INCLUDE_PDF_BER_NOTES,
          payload: STRING_TRUE
        });
      } else {
        dispatchReactPDF({
          type: INCLUDE_PDF_BER_NOTES,
          payload: STRING_FALSE
        });
      }

      const bereavementPDF = {
        widgetName: Bereavement,
        patientNumber: patient.patientNumber,
        pim,
        dispatchReactPDF
      };
      asyncWidgetPayload.push(bereavementPDF);
    }

    if (widgetConfig && widgetConfig.includes(CarePlanProblems)) {
      const CarePlanProblemsPrint = printConfig === null || printConfig === void 0 ? void 0 : printConfig.CarePlanProblems;
      const carePlanProblemsPDF = {
        widgetName: CarePlanProblems,
        patientNumber: patient.patientNumber,
        latest: 'print',
        mode: CarePlanProblemsPrint && CarePlanProblemsPrint.view === Contents ? ANSWERED : NONE,
        agency,
        dispatchReactPDF,
        observation,
        fromDate: fromDateFormatted,
        toDate: toDateFormatted
      };
      asyncWidgetPayload.push(carePlanProblemsPDF);
    }

    if (widgetConfig && widgetConfig.includes(CarePlanFrequency)) {
      const CarePlanFrequencyPrint = printConfig === null || printConfig === void 0 ? void 0 : printConfig.CarePlanFrequency;

      if (notesIncluded(CarePlanFrequencyPrint)) {
        dispatchReactPDF({
          type: INCLUDE_PDF_CPF_NOTES,
          payload: STRING_TRUE
        });
      } else {
        dispatchReactPDF({
          type: INCLUDE_PDF_CPF_NOTES,
          payload: STRING_FALSE
        });
      }

      const carePlanFrequencyPDF = {
        widgetName: CarePlanFrequency,
        carePlans: carePlanFrequenciesGlobal,
        patientStandingOrdersGlobal,
        roleGroupsGlobal,
        patient,
        currentUser,
        agency,
        keycloak,
        getAllRgHashes,
        fromDate: fromDateFormatted,
        toDate: toDateFormatted
      };
      asyncWidgetPayload.push(carePlanFrequencyPDF);
    }

    if (widgetConfig && widgetConfig.includes(PatientAssistance)) {
      const PatientAssistancePrint = printConfig === null || printConfig === void 0 ? void 0 : printConfig.PatientAssistance;

      if (notesIncluded(PatientAssistancePrint)) {
        dispatchReactPDF({
          type: INCLUDE_PDF_TASKS_NOTES,
          payload: STRING_TRUE
        });
      } else {
        dispatchReactPDF({
          type: INCLUDE_PDF_TASKS_NOTES,
          payload: STRING_FALSE
        });
      }

      const patientAssistancePdf = {
        widgetName: PatientAssistance,
        observation,
        patientNumber: patient.patientNumber,
        agency,
        siteId: patient.siteId.toString(),
        dispatchReactPDF,
        fromDate: fromDateFormatted,
        toDate: toDateFormatted
      };
      asyncWidgetPayload.push(patientAssistancePdf);
    }

    if (widgetConfig && widgetConfig.includes(Measures)) {
      const measuresPDF = {
        widgetName: Measures,
        patientNumber: patient.patientNumber,
        dispatchReactPDF,
        dispatchGlobalContext: globalContext.dispatch,
        observation,
        fromDate: fromDateFormatted,
        toDate: toDateFormatted
      };
      asyncWidgetPayload.push(measuresPDF);
    }

    if (widgetConfig && widgetConfig.includes(ClearScripts)) {
      const MedicationsPrint = printConfig === null || printConfig === void 0 ? void 0 : printConfig.ClearScripts;

      if (notesIncluded(MedicationsPrint)) {
        dispatchReactPDF({
          type: INCLUDE_PDF_MEDICATIONS_NOTES,
          payload: STRING_TRUE
        });
      } else {
        dispatchReactPDF({
          type: INCLUDE_PDF_MEDICATIONS_NOTES,
          payload: STRING_FALSE
        });
      }

      const clearscriptsPDF = {
        widgetName: ClearScripts,
        patientNumber: patient.patientNumber,
        dispatchReactPDF,
        clearscripts,
        fromDate: fromDateFormatted,
        toDate: toDateFormatted,
        agency,
        printView: MedicationsPrint === null || MedicationsPrint === void 0 ? void 0 : MedicationsPrint.view
      };
      asyncWidgetPayload.push(clearscriptsPDF);
    }

    if (widgetConfig && widgetConfig.includes(DocLink)) {
      dispatchReactPDF({
        type: INCLUDE_PDF_CERT_APP,
        payload: STRING_TRUE
      });
    }

    if (widgetConfig && widgetConfig.includes(ProgressNotes)) {
      const progressNotesPDF = {
        widgetName: ProgressNotes,
        observation,
        dispatchReactPDF,
        patientNumber: patient.patientNumber,
        agency,
        fromDate: fromDateFormatted,
        toDate: toDateFormatted
      };
      asyncWidgetPayload.push(progressNotesPDF);
    }

    return asyncWidgetPayload;
  };

  const printDaChart = (printConfig, widgetConfig) => {
    const {
      fromDate,
      toDate
    } = printConfig;
    setWidgetConfig(widgetConfig);
    setPrintConfig(printConfig);
    getAsyncPdfData(getWidgetConfig(printConfig, widgetConfig, fromDate, toDate));
    setLoading(true);
  };

  const executePrint = () => {
    const getDoc = () => /*#__PURE__*/React.createElement(DashboardPDFDocument, {
      patient: patient,
      currentUser: currentUser,
      widgetConfig: localWidgetConfig,
      rgHash: pdfRgHash,
      rgHashPrint: pdfRgHashPrint,
      dashboardPrintConfig: localPrintConfig,
      patientCertHistory: patientCertHistory,
      cpfNotes: pdfCpfNotes,
      cppNotesGlobal: cppNotesGlobal,
      pdfCarePlanProblems: pdfCarePlanProblems,
      pdfProgressNotes: pdfProgressNotes,
      pdfPSOComponents: pdfPSOComponents,
      pdfCertAppSnapshot: pdfCertAppSnapshot,
      pdfMeasuresGraphImages: pdfMeasuresGraphImages,
      measuresGlobal: measuresGlobal,
      pdfMedications: pdfMedications,
      pdfMedicationsNotes: pdfMedicationsNotes,
      pdfBereavNotes: pdfBereavNotes,
      pdfCareGiversInfo: pdfCareGiversInfo,
      historicalRows: historicalRows,
      jsonMeasures: jsonMeasures,
      pdfWeeklyTasks: pdfWeeklyTasks,
      includePdfTasksNotes: includePdfTasksNotes,
      pdfTasksNotes: pdfTasksNotes,
      pdfFacesheetData: pdfFacesheetData
    });

    printPDF(getDoc, patient).then(() => {
      setWidgetConfig([]);
      clearPdfData(dispatchReactPDF);
      setLoading(false);
    });
  };
  /*
    Same pattern as this code block in Dashboard/index:
     useEffect(() => {
    const allAsyncPDFDataRetrieved =
      widgetConfig &&
      widgetConfig.every((component) =>
        asyncPdfDataReady.includes(component as string),
      );
    if (allAsyncPDFDataRetrieved && printInterfaceOpen) {
      printPDF(getDoc, patient).then(() => {
        dispatch({
          type: SET_LOADER,
          payload: false,
        });
        dispatch({
          type: SET_PRINT_INTERFACE_OPEN,
          payload: false,
        });
        closePrintDialog();
      });
    }
     Tap into the asyncPdfDataReady logic from MummsPDFGlobalContext. The check for 
    'printInterfaceOpen' is new in this PR to avoid above code triggering when not printing
    from PrintInterface (i.e. from here).
  */


  useEffect(() => {
    if (localWidgetConfig.length > 0 && localWidgetConfig.every(e => asyncPdfDataReady.includes(e))) {
      executePrint();
    }
  }, [asyncPdfDataReady]);
  return /*#__PURE__*/React.createElement(PrintPDFContext.Provider, {
    value: {
      printDaChart
    }
  }, /*#__PURE__*/React.createElement(DialogBox, {
    handleClick: () => setLoading(false),
    open: loading,
    hideBox: true,
    hideBackdrop: true
  }, _ref), children);
};